import {
  OrganizationService,
  PaymentMethodTypesEnum,
  TransactionFlowTypesEnum,
} from "@propertelligent/client-api";
import { FormProvider, useForm } from "react-hook-form";
import { useEffect, useMemo } from "react";
import RightSideModal from "../Modal/right-side-modal";
import InputField from "../form-fields/InputField";
import SelectField from "../form-fields/SelectField";
import Card from "../card";
import apiHelper from "../../utils/apiHelper";
import { NotificationType, PubSub, TopicsEnum } from "../../utils/pubsub";
import DateHelper from "../../utils/DateHelper";
import { getEnumOptions } from "../../utils/enumHelpers";
import type { TransactionRow } from "./transactionTypes";

interface DropdownOption {
  id?: number | null;
  accountName?: string | null;
}

interface EditTransactionDrawerProps {
  isOpen: boolean;
  transaction: TransactionRow | null;
  paymentMethods: DropdownOption[];
  payeeAccounts: DropdownOption[];
  onClose: () => void;
  onSaved: () => void;
}

interface EditTransactionFormValues {
  description: string;
  paymentMethodId: string;
  payeeAccountId: string;
  tranactionCreditDebitTypeId: string;
  amount: string;
  paymentDate: string;
  reference: string;
  transactionId: string;
}

const formId = "edit-transaction-form";
const TRANSACTION_AMOUNT_MAX = 99999999.99;
const TRANSACTION_AMOUNT_MAX_MESSAGE =
  "Amount cannot exceed 99,999,999.99.";

const toDateInputValue = (paymentDate?: string) =>
  paymentDate ? DateHelper.convertUTCtoDateTime2(paymentDate) : "";

const toDefaultValues = (
  transaction: TransactionRow | null,
): EditTransactionFormValues => ({
  description: transaction?.description ?? "",
  paymentMethodId: transaction?.paymentMethodId
    ? String(transaction.paymentMethodId)
    : "",
  payeeAccountId: transaction?.payeeAccountId
    ? String(transaction.payeeAccountId)
    : "",
  tranactionCreditDebitTypeId: transaction?.tranactionCreditDebitTypeId != null
    ? String(transaction.tranactionCreditDebitTypeId)
    : "",
  amount:
    transaction?.amount != null && !Number.isNaN(Number(transaction.amount))
      ? String(transaction.amount)
      : "",
  paymentDate: toDateInputValue(transaction?.paymentDate),
  reference: transaction?.reference ?? "",
  transactionId: transaction?.transactionId ?? "",
});

const EditTransactionDrawer = ({
  isOpen,
  transaction,
  paymentMethods,
  payeeAccounts,
  onClose,
  onSaved,
}: EditTransactionDrawerProps) => {
  const methods = useForm<EditTransactionFormValues>({
    mode: "onChange",
    defaultValues: toDefaultValues(transaction),
  });

  useEffect(() => {
    methods.reset(toDefaultValues(transaction));
  }, [methods, transaction]);

  const paymentMethodOptions = useMemo(() => {
    const connectableOptions = paymentMethods
      .filter((item) => Number(item?.id) > 0 && item?.accountName)
      .map((item) => ({
        Id: Number(item.id),
        Name: item.accountName as string,
      }));

    // Cash/Check/Waived (and other non-Plaid recording modes) reference a seeded
    // internal payment method that's never part of the org's connectable-accounts
    // list above, so it's silently absent here and the SelectField renders blank
    // for those transactions. Inject the transaction's own current value so it
    // always displays, even though it isn't one of the selectable connected accounts.
    const currentId = transaction?.paymentMethodId;
    const hasCurrentOption = currentId
      ? connectableOptions.some((option) => option.Id === currentId)
      : true;

    if (currentId && !hasCurrentOption) {
      return [
        { Id: currentId, Name: transaction?.paymentMethodAccountName ?? "—" },
        ...connectableOptions,
      ];
    }

    return connectableOptions;
  }, [paymentMethods, transaction?.paymentMethodId, transaction?.paymentMethodAccountName]);

  const payeeAccountOptions = useMemo(
    () =>
      payeeAccounts
        .filter((item) => Number(item?.id) > 0 && item?.accountName)
        .map((item) => ({
          Id: Number(item.id),
          Name: item.accountName as string,
        })),
    [payeeAccounts],
  );

  const transactionFlowOptions = useMemo(
    () =>
      getEnumOptions(
        TransactionFlowTypesEnum,
        Object.values(TransactionFlowTypesEnum).filter(
          (value): value is number => typeof value === "number",
        ),
      ),
    [],
  );

  const {
    handleSubmit,
    formState: { isDirty, isSubmitting, isValid },
  } = methods;

  const onSubmit = async (values: EditTransactionFormValues) => {
    if (!transaction?.id) {
      return;
    }

    const payload = {
      id: transaction.id,
      description: values.description.trim(),
      paymentMethodId: Number(values.paymentMethodId),
      payeeAccountId: Number(values.payeeAccountId),
      tranactionCreditDebitTypeId: Number(values.tranactionCreditDebitTypeId),
      amount: Number(values.amount),
      paymentDate: new Date(values.paymentDate).toISOString(),
      reference: values.reference.trim(),
      transactionId: values.transactionId.trim(),
      paymentDueAccountId: transaction.paymentDueAccountId,
      paymentMethodTypeId: transaction.paymentMethodTypeId,
      assistanceProvider: transaction.assistanceProvider ?? null,
      paymentMethodAccountName: transaction.paymentMethodAccountName ?? null,
      payeeAccountName: transaction.payeeAccountName ?? null,
    };

    try {
      const response = await apiHelper(
        OrganizationService.putApiOrganizationTransaction,
        {
          successMessage: "Successfully Updated Transaction",
          showNotification: true,
        },
        String(transaction.id),
        payload,
      );

      if (response?.data?.success) {
        onClose();
        onSaved();
        return;
      }

      if (!response?.statusCode && !response?.errors) {
        PubSub.publish(TopicsEnum.Notification.toString(), {
          type: NotificationType.Failure,
          message: "Failed to update transaction. Try again.",
        });
      }
    } catch (error) {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        type: NotificationType.Failure,
        message: "Failed to update transaction. Try again.",
      });
    }
  };

  return (
    <RightSideModal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Transaction"
      buttonlabel={isSubmitting ? "Saving..." : "Save"}
      formId={formId}
      maxWidth={550}
      isDirty={isDirty}
      isLoading={isSubmitting}
      isSubmitDisabled={isSubmitting || !isDirty || !isValid}
    >
      <Card className="no-bg">
        <FormProvider {...methods}>
          <form id={formId} onSubmit={handleSubmit(onSubmit)}>
            <div className="row">
              <div className="col-md-12 mb-1">
                <InputField
                  name="description"
                  label="Description"
                  placeholder="Enter Description"
                  allowSpecialChars={true}
                />
              </div>

              <div className="col-md-12 mb-1">
                <SelectField
                  name="paymentMethodId"
                  label="Payment Method"
                  options={paymentMethodOptions}
                  placeholderText="Select Payment Method"
                />
              </div>

              <div className="col-md-12 mb-1">
                <SelectField
                  name="payeeAccountId"
                  label="Payee Account"
                  options={payeeAccountOptions}
                  placeholderText="Select Payee Account"
                />
              </div>

              <div className="col-md-12 mb-1">
                <SelectField
                  name="tranactionCreditDebitTypeId"
                  label="Category"
                  options={transactionFlowOptions}
                  placeholderText="Select Category"
                />
              </div>

              <div className="col-md-6 mb-1">
                <InputField
                  name="amount"
                  label="Amount"
                  type="number"
                  required
                  requiredMessage="Amount is required"
                  placeholder="Enter Amount"
                  validation={{
                    validate: (value) => {
                      const amount = Number(value);
                      if (!(amount > 0)) {
                        return "Amount must be greater than 0";
                      }
                      if (amount > TRANSACTION_AMOUNT_MAX) {
                        return TRANSACTION_AMOUNT_MAX_MESSAGE;
                      }
                      return true;
                    },
                  }}
                  min="0.01"
                  allowDecimal={true}
                />
              </div>

              <div className="col-md-6 mb-1">
                <InputField
                  name="paymentDate"
                  label="Date"
                  type="date"
                  required
                  requiredMessage="Date is required"
                />
              </div>

              <div className="col-md-12 mb-1">
                <InputField
                  name="reference"
                  label="Reference"
                  placeholder="Enter Reference"
                  allowSpecialChars={true}
                />
              </div>

              <div className="col-md-12 mb-1">
                <InputField
                  name="transactionId"
                  label="Transaction ID"
                  required={
                    transaction?.paymentMethodTypeId ===
                    PaymentMethodTypesEnum.Other
                  }
                  requiredMessage="Transaction ID is required"
                  placeholder="Enter Transaction ID"
                  allowSpecialChars={true}
                />
              </div>
            </div>
          </form>
        </FormProvider>
      </Card>
    </RightSideModal>
  );
};

export default EditTransactionDrawer;
